import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Upload,
  X,
  Image as ImageIcon,
  DollarSign,
  MapPin,
  Navigation,
  Info,
  Loader2,
  GripVertical,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { btn } from '../../utils/buttonStyles';
import { useLocation } from '../../contexts/LocationContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import GooglePlacesAutocomplete from '../../components/common/GooglePlacesAutocomplete';
import marketplaceAPI from '../services/marketplaceService';
import { apiCall } from '../../services/apiUtil';
import { uploadFile, moveImage, handleUploadError, ContentPolicyError } from '../../services/imageUploadService';
import { useImagePicker } from '../../hooks/useImagePicker.jsx';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import { getConfig } from '../../config';
import toast from 'react-hot-toast';

const conditions = [
  { value: 'new', label: 'New', description: 'Never used, with tags' },
  { value: 'like_new', label: 'Like New', description: 'Used once or twice' },
  { value: 'good', label: 'Good', description: 'Gently used, minor wear' },
  { value: 'fair', label: 'Fair', description: 'Noticeable wear but functional' },
  { value: 'poor', label: 'Poor', description: 'Heavy wear, still usable' },
];

const MAX_IMAGES = 5;

export default function SellGearPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { location: userLocation } = useLocation();

  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]); // { file?, s3Key?, preview }
  const [uploadingImages, setUploadingImages] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    categoryId: '',
    condition: 'good',
    brand: '',
    model: '',
    yearPurchased: '',
    locationText: '',
    locationAddress: '',
    locationPlaceId: '',
    locationCity: '',
    locationState: '',
    locationCountry: '',
    latitude: null,
    longitude: null,
    isNegotiable: false,
    shippingAvailable: false,
    pickupOnly: true,
  });

  const [errors, setErrors] = useState({});

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await marketplaceAPI.getCategories();
        setCategories(data);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load existing listing for edit mode
  useEffect(() => {
    if (!isEditMode) return;

    const loadListing = async () => {
      try {
        const listing = await marketplaceAPI.getListingById(parseInt(id));
        if (!listing || listing.status === 'deleted') {
          toast.error('Listing not found');
          navigate('/marketplace');
          return;
        }

        // Check ownership
        if (listing.seller?.id !== user?.id) {
          toast.error('You can only edit your own listings');
          navigate('/marketplace');
          return;
        }

        setFormData({
          title: listing.title || '',
          description: listing.description || '',
          price: listing.price?.toString() || '',
          categoryId: listing.category?.id?.toString() || '',
          condition: listing.condition || 'good',
          brand: listing.brand || '',
          model: listing.model || '',
          yearPurchased: listing.yearPurchased > 0 ? String(listing.yearPurchased) : '',
          locationText: listing.locationText || '',
          locationAddress: listing.locationAddress || '',
          locationPlaceId: listing.locationPlaceId || '',
          locationCity: listing.locationCity || '',
          locationState: listing.locationState || '',
          locationCountry: listing.locationCountry || '',
          latitude: listing.latitude,
          longitude: listing.longitude,
          isNegotiable: listing.isNegotiable || false,
          shippingAvailable: listing.shippingAvailable || false,
          pickupOnly: listing.pickupOnly !== false,
        });

        // Load existing images — resolve the stored /images/* path through
        // resolveImageUrl so it hits the CDN/backend correctly in all environments
        if (listing.images?.length > 0) {
          setImages(
            listing.images.map((img) => ({
              s3Key: img.s3Key,
              preview: resolveImageUrl(img.url),
            }))
          );
        }
      } catch (error) {
        console.error('Failed to load listing:', error);
        toast.error('Failed to load listing');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };
    loadListing();
  }, [id, isEditMode, navigate, user]);

  // Set location from context
  useEffect(() => {
    if (userLocation && !formData.locationText) {
      setFormData((prev) => ({
        ...prev,
        locationText: userLocation.city
          ? `${userLocation.city}, ${userLocation.state}`
          : '',
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      }));
    }
  }, [userLocation, formData.locationText]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const { openPicker, pickerInput } = useImagePicker({
    accept: 'image/*',
    multiple: true,
    onFiles: handleImageSelect,
  });

  async function handleImageSelect(files) {
    if (!files.length) return;

    const remainingSlots = MAX_IMAGES - images.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const filesToAdd = files.slice(0, remainingSlots);
    setUploadingImages(true);

    // Upload + moderate all selected images in parallel
    const results = await Promise.allSettled(
      filesToAdd.map((file) => uploadFile(file, 'marketplace_assets', file.name))
    );

    setUploadingImages(false);

    let policyViolation = false;
    const approved = [];

    for (const result of results) {
      if (result.status === 'fulfilled') {
        approved.push({ tempUrl: result.value.url, preview: resolveImageUrl(result.value.url) });
      } else {
        if (result.reason instanceof ContentPolicyError) {
          policyViolation = true;
        } else {
          toast.error(result.reason?.message || 'Failed to upload image');
        }
      }
    }

    if (policyViolation) {
      window.dispatchEvent(new CustomEvent('content-policy-violation'));
    }

    if (approved.length > 0) {
      setImages((prev) => [...prev, ...approved]);
    }
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setImages((prev) => {
      const newImages = [...prev];
      const draggedImage = newImages[draggedIndex];
      newImages.splice(draggedIndex, 1);
      newImages.splice(index, 0, draggedImage);
      return newImages;
    });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Move approved temp images to canonical paths now that we have the listingId.
  // Existing images (s3Key already set) are passed through unchanged.
  const finalizeImages = async (listingId) => {
    const result = [];
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      if (img.s3Key) {
        // Strip /images/ prefix — controller will re-add it when saving to DB
        const cleanKey = img.s3Key.replace(/^\/images\//, '');
        result.push({ s3Key: cleanKey });
      } else if (img.tempUrl) {
        const { key } = await moveImage(img.tempUrl, `marketplace_assets/${listingId}/${i + 1}.jpeg`);
        result.push({ s3Key: key });
      }
    }
    return result;
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Please enter a valid price';
    }
    if (!formData.categoryId) {
      newErrors.categoryId = 'Please select a category';
    }
    if (!formData.condition) {
      newErrors.condition = 'Please select a condition';
    }
    // Check for images - either new files to upload or existing s3Keys
    const hasImages = images.some((img) => img.tempUrl || img.s3Key);
    if (!hasImages) {
      newErrors.images = 'Please add at least one image';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Build listing data - shared between create and edit
  const buildListingData = (imageData = []) => {
    return {
      title: formData.title,
      description: formData.description,
      price: parseFloat(formData.price),
      categoryId: parseInt(formData.categoryId),
      condition: formData.condition,
      brand: formData.brand,
      model: formData.model,
      yearPurchased: parseInt(formData.yearPurchased) > 0 ? parseInt(formData.yearPurchased) : null,
      locationText: formData.locationText || '',
      locationAddress: formData.locationAddress || '',
      locationPlaceId: formData.locationPlaceId || '',
      locationCity: formData.locationCity || '',
      locationState: formData.locationState || '',
      locationCountry: formData.locationCountry || '',
      latitude: formData.latitude ?? null,
      longitude: formData.longitude ?? null,
      isNegotiable: formData.isNegotiable,
      shippingAvailable: formData.shippingAvailable,
      pickupOnly: formData.pickupOnly,
      images: imageData,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setSubmitting(true);
    try {
      if (isEditMode) {
        const listingId = parseInt(id);
        const imageData = await finalizeImages(listingId);
        const listingData = buildListingData(imageData);
        await marketplaceAPI.updateListing(listingId, listingData);
        toast.success('Listing updated!');
        navigate('/marketplace');
      } else {
        // Create listing first to get the ID, then move temp images to canonical paths
        const newListing = await marketplaceAPI.createListing(buildListingData([]));
        const listingId = newListing.id;
        const imageData = await finalizeImages(listingId);
        await marketplaceAPI.updateListing(listingId, { images: imageData });
        toast.success('Listing created!');
        navigate(`/marketplace/listing/${listingId}`);
      }
    } catch (error) {
      console.error('Failed to save listing:', error);
      toast.error(error.message || 'Failed to save listing');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    navigate('/signin');
    return null;
  }

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} py-8`}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-32 ${theme.backgroundSecondary} rounded" />
            <div className="h-40 ${theme.backgroundSecondary} rounded" />
            <div className="h-12 ${theme.backgroundSecondary} rounded" />
            <div className="h-12 ${theme.backgroundSecondary} rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb
            items={[
              { label: 'Home', path: '/' },
              { label: 'Marketplace', path: '/marketplace' },
              { label: isEditMode ? 'Edit Listing' : 'Sell Item' }
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-2xl font-bold ${theme.text}`}>
            {isEditMode ? 'Edit Listing' : 'Sell Your Gear'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Images Section */}
          <div className={`${theme.card} rounded-lg p-6`}>
            <label className={`block text-sm font-medium mb-3 ${theme.text}`}>
              Photos ({images.length}/{MAX_IMAGES})
              <span className="text-red-500 ml-1">*</span>
            </label>

            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {images.map((img, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
                    draggedIndex === index
                      ? 'border-blue-500 opacity-50'
                      : theme.border
                  } cursor-move group`}
                >
                  <img
                    src={img.preview}
                    alt={`Upload ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-opacity flex items-center justify-center">
                    <GripVertical className="w-6 h-6 text-white opacity-0 group-hover:opacity-100" />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {index === 0 && (
                    <span className="absolute bottom-1 left-1 px-2 py-0.5 text-xs bg-blue-600 text-white rounded">
                      Cover
                    </span>
                  )}
                </div>
              ))}

              {images.length < MAX_IMAGES && (
                <button
                  type="button"
                  onClick={openPicker}
                  className={`aspect-square rounded-lg border-2 border-dashed ${theme.border} flex flex-col items-center justify-center gap-1 hover:border-blue-500 transition-colors ${theme.textSecondary}`}
                >
                  <Upload className="w-6 h-6" />
                  <span className="text-xs">Add</span>
                </button>
              )}
            </div>

            {pickerInput}

            {errors.images && (
              <p className="mt-2 text-sm text-red-500">{errors.images}</p>
            )}

            <p className={`mt-2 text-xs ${theme.textSecondary}`}>
              Drag to reorder. First image will be the cover.
            </p>
          </div>

          {/* Title & Description */}
          <div className={`${theme.card} rounded-lg p-6 space-y-4`}>
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme.text}`}>
                Title<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Wilson Pro Staff Tennis Racket"
                maxLength={200}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.title ? 'border-red-500' : theme.border
                } ${theme.input} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500">{errors.title}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${theme.text}`}>
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your item - condition details, why you're selling, etc."
                rows={4}
                className={`w-full px-4 py-3 rounded-lg border ${theme.border} ${theme.input} focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
              />
            </div>
          </div>

          {/* Price & Category */}
          <div className={`${theme.card} rounded-lg p-6 space-y-4`}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.text}`}>
                  Price<span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <DollarSign
                    className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${theme.textSecondary}`}
                  />
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                      errors.price ? 'border-red-500' : theme.border
                    } ${theme.input} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                </div>
                {errors.price && (
                  <p className="mt-1 text-sm text-red-500">{errors.price}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.text}`}>
                  Category<span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.categoryId ? 'border-red-500' : theme.border
                  } ${theme.input} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.categoryName}
                    </option>
                  ))}
                </select>
                {errors.categoryId && (
                  <p className="mt-1 text-sm text-red-500">{errors.categoryId}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isNegotiable"
                name="isNegotiable"
                checked={formData.isNegotiable}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="isNegotiable" className={theme.text}>
                Price is negotiable (OBO)
              </label>
            </div>
          </div>

          {/* Condition */}
          <div className={`${theme.card} rounded-lg p-6`}>
            <label className={`block text-sm font-medium mb-3 ${theme.text}`}>
              Condition<span className="text-red-500 ml-1">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {conditions.map((cond) => (
                <button
                  key={cond.value}
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, condition: cond.value }))
                  }
                  className={`p-3 rounded-lg border text-center transition-colors ${
                    formData.condition === cond.value
                      ? `border-blue-500 ${theme.backgroundSecondary}`
                      : `${theme.border} ${theme.cardHover}`
                  }`}
                >
                  <span className={`block text-sm font-medium ${theme.text}`}>
                    {cond.label}
                  </span>
                  <span className={`text-xs ${theme.textSecondary}`}>
                    {cond.description}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Optional Details */}
          <div className={`${theme.card} rounded-lg p-6 space-y-4`}>
            <h3 className={`font-medium ${theme.text}`}>Optional Details</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.text}`}>
                  Brand
                </label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  placeholder="e.g., Nike, Wilson"
                  className={`w-full px-4 py-3 rounded-lg border ${theme.border} ${theme.input} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.text}`}>
                  Model
                </label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  placeholder="e.g., Air Max, Pro Staff"
                  className={`w-full px-4 py-3 rounded-lg border ${theme.border} ${theme.input} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${theme.text}`}>
                Year Purchased
              </label>
              <input
                type="number"
                name="yearPurchased"
                value={formData.yearPurchased}
                onChange={handleChange}
                placeholder="e.g., 2023"
                min="1990"
                max={new Date().getFullYear()}
                className={`w-full px-4 py-3 rounded-lg border ${theme.border} ${theme.input} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
          </div>

          {/* Location & Shipping */}
          <div className={`${theme.card} rounded-lg p-6 space-y-4`}>
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme.text}`}>
                Location
              </label>
              <div className="space-y-2">
                <GooglePlacesAutocomplete
                  googleApiKey={getConfig('GOOGLE_PLACES_API_KEY')}
                  placeholder="Search for address..."
                  theme={theme}
                  initialValue={formData.locationAddress || formData.locationText || ''}
                  onAddressSelect={(addressData) => {
                    const city = addressData.city || '';
                    const state = addressData.state || '';
                    const locationText = city && state ? `${city}, ${state}` : addressData.fullAddress;
                    setFormData(prev => ({
                      ...prev,
                      locationText,
                      locationAddress: addressData.fullAddress,
                      locationPlaceId: addressData.placeId || '',
                      locationCity: city,
                      locationState: state,
                      locationCountry: addressData.country || '',
                      latitude: addressData.coordinates?.lat || null,
                      longitude: addressData.coordinates?.lng || null,
                    }));
                  }}
                />
                {formData.locationAddress && (
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${theme.backgroundSecondary} border ${theme.border}`}>
                    <div className="flex items-center gap-x-2 min-w-0">
                      <MapPin className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <div className="min-w-0">
                        {formData.locationText && (
                          <p className={`text-xs font-medium ${theme.text} truncate`}>{formData.locationText}</p>
                        )}
                        {formData.locationAddress !== formData.locationText && (
                          <p className={`text-xs ${theme.textSecondary} truncate`}>{formData.locationAddress}</p>
                        )}
                        {(formData.locationCity || formData.locationState || formData.locationCountry) && (
                          <p className={`text-xs ${theme.textSecondary} truncate`}>
                            {[formData.locationCity, formData.locationState, formData.locationCountry].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-x-2 flex-shrink-0 ml-2">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.locationAddress)}${formData.locationPlaceId ? `&query_place_id=${formData.locationPlaceId}` : ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-x-1 text-xs text-blue-500 hover:text-blue-600"
                        title="Open in Google Maps"
                      >
                        <Navigation className="w-3 h-3" />
                        <span>Navigate</span>
                      </a>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, locationText: '', locationAddress: '', locationPlaceId: '', locationCity: '', locationState: '', locationCountry: '', latitude: null, longitude: null }))}
                        className={`text-xs ${theme.textMuted} hover:text-red-500`}
                        title="Clear location"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="pickupOnly"
                  name="pickupOnly"
                  checked={formData.pickupOnly}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="pickupOnly" className={theme.text}>
                  Local pickup only
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="shippingAvailable"
                  name="shippingAvailable"
                  checked={formData.shippingAvailable}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="shippingAvailable" className={theme.text}>
                  Shipping available
                </label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={`flex-1 ${btn.base} ${btn.lg} ${theme.buttonGhost}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || uploadingImages}
              className={`flex-1 flex items-center justify-center gap-2 ${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
            >
              {(submitting || uploadingImages) && (
                <Loader2 className="w-5 h-5 animate-spin" />
              )}
              {uploadingImages
                ? 'Uploading images...'
                : submitting
                ? 'Saving...'
                : isEditMode
                ? 'Update Listing'
                : 'Create Listing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
